// src/pages/wizard/Step4Entities.jsx
import React from 'react';
import { Check, Database, GitBranch, FileText, Users, MessageSquare, Calendar } from 'lucide-react';

// Connector-specific entities
export const CONNECTOR_ENTITIES = {
  github: [
    { id: 'issues', name: 'Issues', description: 'Track bugs, features, and tasks', icon: FileText },
    { id: 'pulls', name: 'Pull Requests', description: 'Code review workflow', icon: GitBranch },
    { id: 'repos', name: 'Repositories', description: 'Code repositories and metadata', icon: Database },
    { id: 'commits', name: 'Commits', description: 'Code commits and changes', icon: GitBranch },
    { id: 'pull_reviews', name: 'PR Reviews', description: 'Pull request reviews', icon: FileText },
    { id: 'issue_comments', name: 'Issue Comments', description: 'Comments on issues', icon: MessageSquare },
  ],
  jira: [
    { id: 'issues', name: 'Issues', description: 'Jira tickets and tasks', icon: FileText },
    { id: 'projects', name: 'Projects', description: 'Jira projects', icon: Database },
    { id: 'sprints', name: 'Sprints', description: 'Sprint data and progress', icon: Calendar },
    { id: 'users', name: 'Users', description: 'Team members and assignees', icon: Users },
    { id: 'boards', name: 'Boards', description: 'Kanban and Scrum boards', icon: Database },
    { id: 'comments', name: 'Comments', description: 'Issue comments and activity', icon: MessageSquare },
    { id: 'epics', name: 'Epics', description: 'Large bodies of work and initiatives', icon: FileText },
  ],
  slack: [
    { id: 'channels', name: 'Channels', description: 'Slack channels and metadata', icon: MessageSquare },
    { id: 'messages', name: 'Messages', description: 'Channel messages', icon: MessageSquare },
    { id: 'users', name: 'Users', description: 'Workspace members', icon: Users },
    { id: 'files', name: 'Files', description: 'Shared files and attachments', icon: FileText },
    { id: 'reactions', name: 'Reactions', description: 'Message reactions', icon: MessageSquare },
  ],
  teams: [
    { id: 'channels', name: 'Channels', description: 'Teams channels', icon: MessageSquare },
    { id: 'messages', name: 'Messages', description: 'Channel messages', icon: MessageSquare },
    { id: 'users', name: 'Users', description: 'Team members', icon: Users },
    { id: 'calendar', name: 'Calendar', description: 'Meetings and events', icon: Calendar },
    { id: 'files', name: 'Files', description: 'Shared files', icon: FileText },
  ],
  club_automation: [
    // Raw entities
    { id: 'members', name: 'Members', description: 'Member records and profiles', icon: Users },
    { id: 'checkins', name: 'Check-ins', description: 'Member check-in events', icon: Calendar },
    { id: 'locations', name: 'Locations', description: 'Club locations', icon: Database },
    { id: 'agreements_subscriptions', name: 'Agreements & Subscriptions', description: 'Membership agreements and billing subscriptions', icon: FileText },
    { id: 'groupex_classes', name: 'Group Exercise Classes', description: 'Class schedule and capacity', icon: Calendar },
    { id: 'groupex_participants', name: 'Class Participants', description: 'Class roster and attendance', icon: Users },
    { id: 'schedule_events', name: 'Schedule Events', description: 'Appointments and scheduled sessions', icon: Calendar },
    { id: 'programs', name: 'Programs', description: 'Program offerings', icon: FileText },
    { id: 'instructors', name: 'Instructors', description: 'Class instructors and staff', icon: Users },
    { id: 'payroll', name: 'Payroll', description: 'Staff payroll summary', icon: FileText },
    { id: 'past_due', name: 'Past Due', description: 'Past due balances', icon: FileText },
    { id: 'membership_types', name: 'Membership Types', description: 'Membership and group type setup', icon: Database },
    { id: 'club_members', name: 'Club Members (Bulk)', description: 'Full member roster from bulk reports', icon: Users },
    { id: 'checkins_bulk', name: 'Check-ins (Bulk)', description: 'Check-in history from bulk reports', icon: Calendar },
    { id: 'financial_sales_revenue', name: 'Sales Revenue', description: 'Actual sales transactions and revenue', icon: FileText },
    { id: 'pos_locations', name: 'POS Locations', description: 'Point-of-sale terminal setup', icon: Database },
    { id: 'financial_package_usage', name: 'Package Usage', description: 'Package and session credit usage', icon: FileText },
    // Derived entities
    { id: 'member_lifecycle_summary', name: 'Member Health', description: 'Active members, churn risk, pending cancellations', icon: Users },
    { id: 'attendance_trends_daily', name: 'Attendance Trends', description: 'Daily check-ins and peak times by location', icon: Calendar },
    { id: 'class_utilization', name: 'Class Utilization', description: 'Fill rates, show rates, over/underbooked classes', icon: Database },
    { id: 'billing_summary', name: 'Billing Summary', description: 'Upcoming and past scheduled fees', icon: FileText },
    { id: 'membership_status_history', name: 'Status History', description: 'Membership status transitions over time', icon: FileText },
    { id: 'membership_freeze_cancel_events', name: 'Freeze & Cancel Events', description: 'Membership freezes and cancellations', icon: FileText },
    // KPIs (rollup)
    { id: 'kpis', name: 'KPIs', description: 'Prebuilt headline metrics across membership, classes, attendance, billing', icon: Database },
  ],
  jazzhr: [
    // Raw entities
    { id: 'jobs', name: 'Jobs', description: 'Open and closed job requisitions', icon: FileText },
    { id: 'applicants', name: 'Applicants', description: 'Candidate records and contact info', icon: Users },
    { id: 'applications', name: 'Applications', description: 'Candidate-to-job applications with current stage', icon: FileText },
    { id: 'hires', name: 'Hires', description: 'Completed hires with stage names', icon: Users },
    { id: 'activities', name: 'Activities', description: 'Workflow events and stage-change history feed', icon: Calendar },
    { id: 'users', name: 'Users', description: 'Recruiters and hiring team members', icon: Users },
    { id: 'categories', name: 'Categories', description: 'Tag and category definitions', icon: Database },
    { id: 'applicant_categories', name: 'Applicant Categories', description: 'Candidate tag assignments', icon: Database },
    // Derived entities
    { id: 'hiring_stages', name: 'Hiring Stages', description: 'Stage catalog across the hiring workflow', icon: GitBranch },
    { id: 'application_stage_history', name: 'Stage History', description: 'Per-candidate stage transitions over time', icon: GitBranch },
    { id: 'candidate_lifecycle_summary', name: 'Candidate Lifecycle', description: 'One row per candidate: outcome and time to hire', icon: Users },
    { id: 'hiring_funnel', name: 'Hiring Funnel', description: 'Stage-to-stage conversion rates', icon: GitBranch },
    { id: 'time_to_hire_metrics', name: 'Time to Hire', description: 'Median and mean time to hire by job and recruiter', icon: Calendar },
    { id: 'job_pipeline_health', name: 'Pipeline Health', description: 'Per-job req age, stalls, and bottlenecks', icon: Database },
    // KPIs (rollup)
    { id: 'kpis', name: 'KPIs', description: 'Prebuilt recruiting headline metrics', icon: Database },
  ],
  namely: [
    // Raw entities
    { id: 'employees', name: 'Employees', description: 'Employee profiles: status, title, manager, compensation (tokenized)', icon: Users },
    { id: 'job_titles', name: 'Job Titles', description: 'Job title catalog', icon: FileText },
    { id: 'job_tiers', name: 'Job Tiers', description: 'Job level and tier catalog', icon: Database },
    { id: 'group_types', name: 'Group Types', description: 'Group category definitions (departments, locations, custom)', icon: Database },
    { id: 'groups', name: 'Groups', description: 'Departments, office locations, and custom groups', icon: Users },
    { id: 'teams', name: 'Teams', description: 'Teams and team metadata', icon: Users },
    { id: 'countries', name: 'Countries', description: 'Country and subdivision reference data', icon: Database },
    { id: 'profile_fields', name: 'Profile Fields', description: 'Profile schema metadata and custom field catalog', icon: FileText },
    // Derived entities
    { id: 'employee_change_events', name: 'Change Events', description: 'Hires, terminations, promotions, comp changes tracked over time', icon: GitBranch },
    { id: 'employee_lifecycle_summary', name: 'Employee Lifecycle', description: 'One row per employee: tenure, status, promotions, raises', icon: Users },
    { id: 'headcount_timeseries', name: 'Headcount Trends', description: 'Monthly headcount, hires, and departures', icon: Calendar },
    { id: 'org_hierarchy', name: 'Org Hierarchy', description: 'Reporting tree: depth, span of control, management chains', icon: GitBranch },
    // KPIs (rollup)
    { id: 'kpis', name: 'KPIs', description: 'Prebuilt HR headline metrics: headcount, attrition, comp, diversity', icon: Database },
  ],
};

export default function Step4Entities({ connector, selected, onChange }) {
  const connectorId = connector?.id || 'github';
  const availableEntities = CONNECTOR_ENTITIES[connectorId] || CONNECTOR_ENTITIES.github;

  const toggleEntity = (entityId) => {
    if (selected.includes(entityId)) {
      onChange(selected.filter(id => id !== entityId));
    } else {
      onChange([...selected, entityId]);
    }
  };

  const selectAll = () => {
    onChange(availableEntities.map(e => e.id));
  };

  const deselectAll = () => {
    onChange([]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-2">
          Select Entities to Sync
        </h2>
        <p className="text-sm text-[#a0a0a0]">
          Choose which data types you want to sync from {connector?.name}
        </p>
      </div>

      {/* Select All / Deselect All */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={selectAll}
          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
        >
          Select All
        </button>
        <span className="text-[#2a2a2a]">|</span>
        <button
          type="button"
          onClick={deselectAll}
          className="text-sm text-[#a0a0a0] hover:text-white transition-colors"
        >
          Deselect All
        </button>
      </div>

      {/* Entity Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {availableEntities.map((entity) => {
          const Icon = entity.icon;
          const isSelected = selected.includes(entity.id);

          return (
            <button
              key={entity.id}
              onClick={() => toggleEntity(entity.id)}
              className={`
                p-4 rounded-xl border-2 text-left transition-all
                ${isSelected
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-[#2a2a2a] hover:border-[#3a3a3a] bg-[#1a1a1a]'
                }
              `}
            >
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <div className={`
                  mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                  ${isSelected
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-[#2a2a2a]'
                  }
                `}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>

                {/* Icon */}
                <div className={`
                  w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                  ${isSelected ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-[#0a0a0a] border border-[#2a2a2a]'}
                `}>
                  <Icon className={`w-5 h-5 ${isSelected ? 'text-blue-500' : 'text-[#a0a0a0]'}`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className={`font-semibold mb-1 ${isSelected ? 'text-blue-400' : 'text-white'}`}>
                    {entity.name}
                  </h3>
                  <p className="text-sm text-[#a0a0a0]">
                    {entity.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Selection Summary */}
      {selected.length > 0 && (
        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-sm text-blue-400">
            {selected.length} of {availableEntities.length} entities selected
          </p>
        </div>
      )}

      {selected.length === 0 && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <p className="text-sm text-amber-400">
            Select at least one entity to continue
          </p>
        </div>
      )}
    </div>
  );
}